import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './MessageWindow.css';
import { useNavigate, useParams } from 'react-router-dom';
import InfoPanel from './InfoPanel';
import { useTranslation } from 'react-i18next';
import { 
    FaPaperPlane, 
    FaLightbulb, 
    FaCheck, 
    FaTimes, 
    FaLanguage, 
    FaTruck, 
    FaUser, 
    FaRobot, 
    FaEllipsisV, 
    FaEdit,
    FaFile,
    FaTimesCircle,
    FaInfoCircle,
    FaBox,
    FaShippingFast,
    FaCheckCircle,
    FaBarcode,
    FaFilePdf,
    FaFileImage,
    FaFileAlt,
    FaFileArchive,
    FaFileCode,
    FaTag,
    FaWarehouse,
    FaBuilding,
    FaMapMarkerAlt,
    FaCity,
    FaGlobe,
    FaPhone,
    FaEnvelope,
    FaIdCard,
    FaInfo,
    FaMoneyBillWave,
    FaBoxOpen,
    FaShieldAlt,
    FaReceipt,
    FaComments,
    FaHandPaper,
    FaExclamationTriangle
} from 'react-icons/fa';
import WMSChat from './WMSChat';
import CarriersConversation from './CarriersConversation';
import ManualConversation from './ManualConversation';

const MessageWindow = () => {
    const { t } = useTranslation();
    const { ticketId } = useParams();
    const [ticket, setTicket] = useState(null);
    const [messages, setMessages] = useState([]);
    const [employee, setEmployee] = useState('');
    const [newMessage, setNewMessage] = useState('');
    const [editingMessage, setEditingMessage] = useState(null);
    const [editedContent, setEditedContent] = useState('');
    const [loading, setLoading] = useState(true);
    const [userLoading, setUserLoading] = useState(true);
    const [targetLanguage, setTargetLanguage] = useState('original');
    const [translatedMessages, setTranslatedMessages] = useState({});
    const [customerLoading, setCustomerLoading] = useState(false);
    const [orderLoading, setOrderLoading] = useState(false);
    const navigate = useNavigate();
    const [overlayState, setOverlayState] = useState({ show: false, message: '', success: false });
    const [user, setUser] = useState(null);
    const [clientInfo, setClientInfo] = useState('');
    const [orderDetails, setOrderDetails] = useState('');
    const [summary, setSummary] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [isTranslating, setIsTranslating] = useState(false);
    const [carriersConversation, setCarriersConversation] = useState(null);
    const [continuationMessage, setContinuationMessage] = useState('');
    const [testUserId, setTestUserId] = useState('');
    const [testThreadId, setTestThreadId] = useState('');
    const [testAssistantId, setTestAssistantId] = useState('');
    const [validatingMessage, setValidatingMessage] = useState(false);
    const [activeTab, setActiveTab] = useState('carriers');
    const [showActionMenu, setShowActionMenu] = useState(false);
    const [showLanguageMenu, setShowLanguageMenu] = useState(false);
    const actionMenuRef = useRef(null);

    // State for ticket files
    const [ticketFiles, setTicketFiles] = useState([]);
    const [filesLoading, setFilesLoading] = useState(true);
    // State for file overlay
    const [selectedFile, setSelectedFile] = useState(null);

    const wsRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const pingIntervalRef = useRef(null);

    // Add new state for context data
    const [contextData, setContextData] = useState(null);
    const [contextLoading, setContextLoading] = useState(true);

    // Add new state for warehouse email form
    const [warehouseEmailForm, setWarehouseEmailForm] = useState({
        subject: '',
        content: '',
        recipient_email: ''
    });

    // Add new state for niveau2 email form
    const [niveau2EmailForm, setNiveau2EmailForm] = useState({
        subject: '',
        content: '',
        recipient_email: '',
        selectedFiles: []
    });

    // Add new state for file selection
    const [selectedTicketFiles, setSelectedTicketFiles] = useState([]);

    // Add new state for warehouse conversation
    const [warehouseConversation, setWarehouseConversation] = useState(null);
    const [warehouseLoading, setWarehouseLoading] = useState(true);

    // Dummy conversations for the first 5 experience user tickets
    const dummyTicketConversations = {
        '1': [ // Question about T-Shirt material
            { _id: 'exp_msg_1_1', ticket_id: '1', content: "Hi, what material is the Galaxy T-Shirt made of? I'm looking for something soft.", sender: 'customer', status: 'received', created_at: new Date(Date.now() - 10 * 60000).toISOString() },
            { _id: 'exp_msg_1_2', ticket_id: '1', content: "The Galaxy T-Shirt is made of 100% premium cotton for a soft and comfortable feel. It's known for its breathability.", sender: 'employee', status: 'draft', created_at: new Date(Date.now() - 9 * 60000).toISOString() },
            { _id: 'exp_msg_1_3', ticket_id: '1', content: "Hello! The Galaxy T-Shirt is crafted from 100% premium ringspun cotton, which makes it exceptionally soft and comfortable to wear. It's also quite breathable, perfect for everyday use!", sender: 'employee', status: 'sent', created_at: new Date(Date.now() - 8 * 60000).toISOString() },
            { _id: 'exp_msg_1_4', ticket_id: '1', content: "Great, thanks for the info!", sender: 'customer', status: 'received', created_at: new Date(Date.now() - 7 * 60000).toISOString() }
        ],
        '2': [ // Product damaged on arrival (Mug)
            { _id: 'exp_msg_2_1', ticket_id: '2', content: "My Astronaut Mug arrived broken! The handle is completely snapped off. What can I do? My order number is EXP002.", sender: 'customer', status: 'received', created_at: new Date(Date.now() - 15 * 60000).toISOString() },
            // Simulating an attachment (though not a real one here)
            { _id: 'exp_msg_2_2', ticket_id: '2', content: "Attached: broken_mug.jpg (Image of damaged mug)", sender: 'customer', status: 'received', created_at: new Date(Date.now() - 14 * 60000).toISOString(), metadata: { attachment_name: 'broken_mug.jpg', attachment_type: 'image' } },
            { _id: 'exp_msg_2_3', ticket_id: '2', content: "We're very sorry to hear your mug arrived damaged. We can send a replacement or issue a full refund. Please let us know your preference. No need to return the broken item.", sender: 'employee', status: 'automated', created_at: new Date(Date.now() - 13 * 60000).toISOString() },
            { _id: 'exp_msg_2_4', ticket_id: '2', content: "I'd like a replacement, please.", sender: 'customer', status: 'received', created_at: new Date(Date.now() - 12 * 60000).toISOString() },
            { _id: 'exp_msg_2_5', ticket_id: '2', content: "Thanks for confirming. We've processed a replacement for your Astronaut Mug. It should ship out within 24-48 hours. We apologize again for the inconvenience!", sender: 'employee', status: 'sent', created_at: new Date(Date.now() - 11 * 60000).toISOString() }
        ],
        '3': [ // Inquiry about bulk order discount for Hoodies
            { _id: 'exp_msg_3_1', ticket_id: '3', content: "Hi, I'm interested in ordering about 50 Nebula Hoodies for an event. Do you offer any discounts for bulk purchases?", sender: 'customer', status: 'received', created_at: new Date(Date.now() - 20 * 60000).toISOString() },
            { _id: 'exp_msg_3_2', ticket_id: '3', content: "For orders of 50 Nebula Hoodies, we can offer a 15% discount and free standard shipping. Would you like a formal quote?", sender: 'employee', status: 'draft', created_at: new Date(Date.now() - 19 * 60000).toISOString() },
            { _id: 'exp_msg_3_3', ticket_id: '3', content: "Hello! Thanks for your interest. Yes, we do offer bulk discounts! For an order of 50 Nebula Hoodies, we can provide a 15% discount on the total price and include free standard shipping. Please let me know if you'd like me to prepare a formal quote for you.", sender: 'employee', status: 'sent', created_at: new Date(Date.now() - 18 * 60000).toISOString() },
            { _id: 'exp_msg_3_4', ticket_id: '3', content: "Yes, a formal quote would be great. Thanks!", sender: 'customer', status: 'received', created_at: new Date(Date.now() - 17 * 60000).toISOString() }
        ],
        '4': [ // Cancelling my monthly sock subscription
            { _id: 'exp_msg_4_1', ticket_id: '4', content: "I'd like to cancel my monthly subscription for the Alien Socks. My email is customer@example.com.", sender: 'customer', status: 'received', created_at: new Date(Date.now() - 25 * 60000).toISOString() },
            { _id: 'exp_msg_4_2', ticket_id: '4', content: "Your request to cancel the Alien Socks subscription for customer@example.com has been received. The subscription will be cancelled, and no further charges will be made.", sender: 'employee', status: 'automated', created_at: new Date(Date.now() - 24 * 60000).toISOString() },
            { _id: 'exp_msg_4_3', ticket_id: '4', content: "Hi there, we've successfully cancelled your Alien Socks subscription linked to customer@example.com. You won't be billed for future months. Thanks for being a part of our sock club!", sender: 'employee', status: 'sent', created_at: new Date(Date.now() - 23 * 60000).toISOString() }
        ],
        '5': [ // Inquiry about dimensions for Voyager Backpack
            { _id: 'exp_msg_5_1', ticket_id: '5', content: "Hello, can you tell me the dimensions of the Voyager Backpack? I need to make sure it will fit as a carry-on for my flight.", sender: 'customer', status: 'received', created_at: new Date(Date.now() - 30 * 60000).toISOString() },
            { _id: 'exp_msg_5_2', ticket_id: '5', content: "The Voyager Backpack dimensions are 20in (H) x 14in (W) x 8in (D) or 50cm x 35cm x 20cm. This is generally compliant with most airline carry-on size restrictions. However, we always recommend checking with your specific airline.", sender: 'employee', status: 'draft', created_at: new Date(Date.now() - 29 * 60000).toISOString() },
            { _id: 'exp_msg_5_3', ticket_id: '5', content: "Hi! The Voyager Backpack measures 20 inches in height, 14 inches in width, and 8 inches in depth (approximately 50cm x 35cm x 20cm). This size typically meets most airline carry-on requirements. However, as policies can vary, it's always a good idea to double-check with your specific airline before you travel. Let us know if you need anything else!", sender: 'employee', status: 'sent', created_at: new Date(Date.now() - 28 * 60000).toISOString() },
            { _id: 'exp_msg_5_4', ticket_id: '5', content: "Perfect, those dimensions should work. Thank you!", sender: 'customer', status: 'received', created_at: new Date(Date.now() - 27 * 60000).toISOString() }
        ]
    };

    // Helper to get basic details for dummy tickets '1' through '5'
    const getDummyTicketDetails = (id) => {
        const dummyDashboardTicketsData = {
            '1': { _id: '1', subject: 'Question about T-Shirt material', status: 'new', customer_id: 'cust_exp_1', order: 'EXP_ORD_100', ai_logs: {}, channel_name: 'Instagram', company_name: 'Experience Co.', employee_assigned: 'no', updated_at: new Date().toISOString(), metadata: { reference: 'REF001' } },
            '2': { _id: '2', subject: 'Product damaged on arrival (Mug)', status: 'open', customer_id: 'cust_exp_2', order: 'EXP_ORD_101', ai_logs: {}, channel_name: 'Messenger', company_name: 'Demo Corp', employee_assigned: 'yes', updated_at: new Date(Date.now() - 3600 * 1000 * 24).toISOString(), metadata: { reference: 'REF002' } },
            '3': { _id: '3', subject: 'Inquiry about bulk order discount for Hoodies', status: 'pending', customer_id: 'cust_exp_3', order: 'EXP_ORD_102', ai_logs: {}, channel_name: 'WhatsApp', company_name: 'Test Inc.', employee_assigned: 'no', updated_at: new Date(Date.now() - 3600 * 1000 * 48).toISOString(), metadata: { reference: 'REF003' } },
            '4': { _id: '4', subject: 'Cancelling my monthly sock subscription', status: 'resolved', customer_id: 'cust_exp_4', order: null, ai_logs: {}, channel_name: 'Gmail', company_name: 'Experience Co.', employee_assigned: 'yes', updated_at: new Date(Date.now() - 3600 * 1000 * 72).toISOString(), metadata: { reference: 'REF004' } },
            '5': { _id: '5', subject: 'Inquiry about dimensions for Voyager Backpack', status: 'closed', customer_id: 'cust_exp_5', order: null, ai_logs: {}, channel_name: 'Website', company_name: 'Demo Corp', employee_assigned: 'no', updated_at: new Date(Date.now() - 3600 * 1000 * 96).toISOString(), metadata: { reference: 'REF005' } }
        };
        // Add a default summary if not present in dummyDashboardTicketsData
        const ticketDetail = dummyDashboardTicketsData[id];
        if (ticketDetail && !ticketDetail.summary) {
            ticketDetail.summary = ticketDetail.subject; // Use subject as summary
        }
        return ticketDetail;
    };

    const fetchTicketContext = async () => {
        setContextLoading(true);
        try {
            const token = sessionStorage.getItem('access_token');
            const response = await axios.get(`/api/tickets/${ticketId}/context`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setContextData(response.data);
        } catch (error) {
            console.error('Failed to fetch ticket context:', error);
        } finally {
            setContextLoading(false);
        }
    };

    const fetchWarehouseConversation = async () => {
        setWarehouseLoading(true);
        try {
            const token = sessionStorage.getItem('access_token');
            const response = await axios.get(`/api/tickets/warehouse_conversation/${ticketId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setWarehouseConversation(response.data);
        } catch (error) {
            console.error('Failed to fetch warehouse conversation:', error);
        } finally {
            setWarehouseLoading(false);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
                setShowActionMenu(false);
                setShowLanguageMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchCarriersConversation = async () => {
        try {
            const token = sessionStorage.getItem('access_token');
            const response = await axios.get(`/api/tickets/carriers_conversation/${ticketId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setCarriersConversation(response.data);
        } catch (error) {
            console.error('Failed to fetch carriers conversation:', error);
        }
    };

    const connectWebSocket = () => {
        const token = sessionStorage.getItem('access_token');
        if (!token) return;

        wsRef.current = new WebSocket(`/api/ws?token=${token}`);
        
        wsRef.current.onopen = (event) => {
            console.log('WebSocket connection established:', event);
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
        };

        wsRef.current.onerror = (error) => {
            console.error('WebSocket error:', error);
        };

        wsRef.current.onclose = (event) => {
            console.log('WebSocket connection closed:', event);
            reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
        };

        wsRef.current.onmessage = (event) => {
            console.log('WebSocket message received:', event.data);
            if (event.data === 'ping') {
                wsRef.current.send('pong');
            } else {
                // Fetch messages only to quickly update the message list
                fetchMessages();
            }
        };
    };

    useEffect(() => {
        const initialLoad = async () => {
            await fetchUserData(); // Fetches and sets user
            // Other non-ticket specific initial fetches can go here if any
        };
        initialLoad();

        // WebSocket and ping interval setup will be handled by the useEffect below, dependent on 'user'
        
        return () => {
            // General cleanup for the component if any, not specific to WebSocket anymore here
        };
    }, []); // Runs once on component mount

    // New useEffect to handle WebSocket connection based on user role
    useEffect(() => {
        if (user && user.role !== 'experience') {
            connectWebSocket();
            pingIntervalRef.current = setInterval(() => {
                if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                    wsRef.current.send('ping');
                }
            }, 30000);
        }

        return () => {
            // Cleanup WebSocket and ping interval if they were initialized
            if (pingIntervalRef.current) {
                clearInterval(pingIntervalRef.current);
                pingIntervalRef.current = null;
            }
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
        };
    }, [user]); // Runs when 'user' state changes

    // This useEffect will run when ticketId changes, or when the user state is updated
    // (e.g., after fetchUserData completes).
    useEffect(() => {
        if (user !== undefined && ticketId) { // user can be null (failed login) or an object
            fetchTicketAndMessages();
        }
    }, [ticketId, user]);

    const fetchUserData = async () => {
        setUserLoading(true);
        try {
            const token = sessionStorage.getItem('access_token');
            if (!token) {
                 setUser(null); 
                 setUserLoading(false);
                 navigate('/'); 
                 return;
            }
            const response = await axios.get('/api/users/user', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUser(response.data);
        } catch (error) {
            console.error('Failed to fetch user data:', error);
            setUser(null); 
            if (error.response && (error.response.status === 401 || error.response.status === 422)) {
                sessionStorage.removeItem('access_token');
                navigate('/');
            }
        } finally {
            setUserLoading(false);
        }
    };

    const fetchTicketAndMessages = async () => {
        console.log('🔥 DEBUG: fetchTicketAndMessages started for ticketId:', ticketId);
        setLoading(true);
        const token = sessionStorage.getItem('access_token');

        try {
            console.log('🔥 DEBUG: User role:', user?.role);
            
            // Handle experience users first and ensure this block always returns.
            if (user && user.role === 'experience') {
                console.log('🔥 DEBUG: Experience user path taken');
                const dummyMessagesForTicket = dummyTicketConversations[ticketId];
                const dummyTicketDetail = getDummyTicketDetails(ticketId);

                if (dummyMessagesForTicket && dummyTicketDetail) {
                    // Specific dummy data found for this ticketId
                    setTicket(dummyTicketDetail);
                    setMessages(dummyMessagesForTicket);
                    setEmployee('Experience Agent');
                    setClientInfo(`Experience Client: ${dummyTicketDetail.company_name || 'N/A'}`);
                    if (dummyTicketDetail.order || (dummyTicketDetail.metadata && dummyTicketDetail.metadata.reference)) {
                        setOrderDetails(`Experience Order Ref: ${dummyTicketDetail.order || dummyTicketDetail.metadata.reference || 'N/A'}`);
                    } else {
                        setOrderDetails('');
                    }
                    setSummary(dummyTicketDetail.summary || dummyTicketDetail.subject);
                    // Clear any potentially API-fetched data for these for experience users
                    setContextData(null);
                    setWarehouseConversation(null);
                    setCarriersConversation(null);
                    setTicketFiles([]);
                    setLoading(false);
                    return; 
                } else {
                    // No specific dummy data for this ticketId (e.g., ticketId '6', '7', etc.)
                    // Or, data was inconsistent (e.g. messages defined but no ticket details)
                    setTicket(null); 
                    setMessages([]);
                    setEmployee('Experience Agent');
                    setClientInfo('');
                    setOrderDetails('');
                    setSummary(t('messageWindow.noDataForExperienceTicket', 'No dummy data for this ticket ID.'));
                    setContextData(null);
                    setWarehouseConversation(null);
                    setCarriersConversation(null);
                    setTicketFiles([]);
                    setLoading(false);
                    return; 
                }
            }

            // --- Non-experience user path (real API calls) ---
            if (!token) {
                console.log('🔥 DEBUG: No token found, redirecting');
                setTicket(null);
                setMessages([]);
                setLoading(false);
                navigate('/');
                return;
            }

            console.log('🔥 DEBUG: Making API call to get ticket messages');
            const response = await axios.get(`/api/tickets/${ticketId}/messages`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            console.log('🔥 DEBUG: API Response received:', response.data);

            if (response.data && response.data.ticket) {
                console.log('🔥 DEBUG: Setting ticket data:', response.data.ticket);
                setTicket(response.data.ticket);
                setMessages(response.data.messages || []);
                setEmployee(response.data.employee || 'N/A');
                
                console.log('🔥 DEBUG: Calling fetchOrderAndCustomerInfo');
                fetchOrderAndCustomerInfo(response.data.ticket, token);
                
                setSummary(response.data.ticket.summary || response.data.ticket.subject || 'No summary available.');
                
                console.log('🔥 DEBUG: Starting additional data fetches');
                await fetchTicketContext();
                await fetchWarehouseConversation();
                await fetchCarriersConversation();
                await fetchTicketFiles();
                console.log('🔥 DEBUG: All additional data fetches completed');
            } else {
                console.log('🔥 DEBUG: NO TICKET DATA FOUND IN RESPONSE');
                setTicket(null);
                setMessages([]);
                console.error('Ticket data not found in API response for ID:', ticketId);
            }
            console.log('🔥 DEBUG: Setting loading to false');
            setLoading(false);
            
        } catch (error) {
            console.error('🔥 DEBUG: ERROR in fetchTicketAndMessages:', error);
            setTicket(null); 
            setMessages([]);
            setLoading(false);
        }
    };

    const fetchMessages = async () => {
        try {
            const token = sessionStorage.getItem('access_token');
            const response = await axios.get(`/api/tickets/${ticketId}/messages`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setMessages(response.data.messages);
        } catch (error) {
            console.error('Failed to fetch messages:', error);
        }
    };

    const fetchOrderAndCustomerInfo = async (ticketData, token) => {
        console.log('🔥 DEBUG: fetchOrderAndCustomerInfo called with:', ticketData);
        
        if (ticketData.customer_id) {
            console.log('🔥 DEBUG: Found customer_id, calling fetchCustomerInfo');
            fetchCustomerInfo(ticketData.customer_id, token);
        } else if (ticketData.customer_email) {
            console.log('🔥 DEBUG: Found customer_email, setting clientInfo directly');
            setClientInfo(`Email: ${ticketData.customer_email}`);
        } else {
            console.log('🔥 DEBUG: No customer info found');
            setClientInfo('');
        }

        if (ticketData.order) {
            console.log('🔥 DEBUG: Found order, calling fetchOrderInfo');
            fetchOrderInfo(ticketData.order, token);
        } else {
            console.log('🔥 DEBUG: No order found');
            setOrderDetails('');
        }
    };

    // New function to fetch ticket files
    const fetchTicketFiles = async () => {
        setFilesLoading(true);
        try {
            const token = sessionStorage.getItem('access_token');
            const response = await axios.get(`/api/tickets/${ticketId}/files`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setTicketFiles(response.data.files);
        } catch (error) {
            console.error('Failed to fetch ticket files:', error);
        } finally {
            setFilesLoading(false);
        }
    };

    const formatMessageContent = (content) => {
        const lines = content.split('\n');
        return lines.map((line, index) => {
            // Regular expression to match URLs ending with .pdf
            const urlRegex = /(https?:\/\/[^\s]+\.pdf)/gi;
            const parts = line.split(urlRegex);
            
            return (
                <span key={index}>
                    {parts.map((part, partIndex) => {
                        if (part.match(urlRegex)) {
                            // Convert http to https
                            const secureUrl = part.replace('http://', 'https://');
                            return (
                                <div key={partIndex}>
                                    <object
                                        data={secureUrl}
                                        type="application/pdf"
                                        width="100%"
                                        height="500px"
                                    >
                                        <p>Unable to display PDF. <a href={secureUrl}>Download PDF</a></p>
                                    </object>
                                </div>
                            );
                        }
                        return part;
                    })}
                    <br />
                </span>
            );
        });
    };
    
    const fetchCustomerInfo = async (customerId, token) => {
        setCustomerLoading(true);
        try {
            const response = await axios.get(`/api/shopify/get_customer`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { customer_id: customerId }
            });
            const customer = response.data;
            setClientInfo(`Phone: ${customer.phone}\nFirst Name: ${customer.first_name}\nLast Name: ${customer.last_name}\nEmail: ${customer.email}`);
        } catch (error) {
            console.error('Failed to fetch customer info:', error);
        } finally {
            setCustomerLoading(false);
        }
    };

    const fetchOrderInfo = async (orderId, token) => {
        setOrderLoading(true);
        try {
            const response = await axios.get(`/api/logistics/edn/order_by_id/${orderId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const order = response.data;
            const formattedOrderDetails = `
Order ID: ${order.order_id}
Reference: ${order.reference}
Status: ${order.status}
Total Price: ${order.grand_total} ${order.currency}
Shipping Cost: ${order.shipping_cost} ${order.currency}
Created At: ${order.created_at}
Customer Email: ${order.customer_email}
Customer Name: ${order.customer_name}
Customer Phone: ${order.customer_phone}
Shipping Address:
    ${order.shipping_address.firstname} ${order.shipping_address.lastname}
    ${order.shipping_address.street1}
    ${order.shipping_address.street2 ? order.shipping_address.street2 + '\n' : ''}
    ${order.shipping_address.postcode} ${order.shipping_address.city}
    ${order.shipping_address.country}
Line Items:
${order.items.map(item => `
Product ID: ${item.product_id}
Name: ${item.name}
SKU: ${item.sku}
Quantity: ${item.quantity}
Price: ${item.price} ${order.currency}
EAN: ${item.ean || 'N/A'}
`).join('\n')}
`;
            setOrderDetails(formattedOrderDetails);
        } catch (error) {
            console.error('Failed to fetch order info:', error);
        } finally {
            setOrderLoading(false);
        }
    };

    const handleSendMessage = async () => {
        try {
            const token = sessionStorage.getItem('access_token');
            await axios.post('/api/messages/add', {
                ticket_id: ticketId,
                content: newMessage,
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setNewMessage('');
            fetchMessages(); // Fetch messages only to update the message list quickly
        } catch (error) {
            console.error('Failed to send message:', error);
        }
    };

    const handleGetAssistantResponse = async () => {
        try {
            const token = sessionStorage.getItem('access_token');
            const aiLogsString = JSON.stringify(ticket.ai_logs);

            const response = await axios.post('/api/messages/writer_assistant', {
                text: newMessage,
                ai_logs: aiLogsString,
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setNewMessage(response.data.response);
        } catch (error) {
            console.error('Failed to get assistant response:', error);
        }
    };

    const handleResolveTicket = async () => {
        setOverlayState({ show: true, message: t('messageWindow.processing'), success: false });
        try {
            const token = sessionStorage.getItem('access_token');
            await axios.put(`/api/tickets/${ticketId}/resolve`, null, {
                headers: { Authorization: `Bearer ${token}` },
            });
            await fetchTicketAndMessages(); // Fetch ticket and messages to update the status and messages
            setOverlayState({ show: true, message: t('messageWindow.resolveSuccess'), success: true });
        } catch (error) {
            console.error('Failed to resolve ticket:', error);
            setOverlayState({ show: true, message: t('messageWindow.resolveError'), success: false });
        } finally {
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 2000);
        }
    };
    
    const handleCloseTicket = async () => {
        setOverlayState({ show: true, message: t('messageWindow.processing'), success: false });
        try {
            const token = sessionStorage.getItem('access_token');
            await axios.put(`/api/tickets/${ticketId}/close`, null, {
                headers: { Authorization: `Bearer ${token}` },
            });
            await fetchTicketAndMessages(); // Fetch ticket and messages to update the status and messages
            setOverlayState({ show: true, message: t('messageWindow.closeSuccess'), success: true });
        } catch (error) {
            console.error('Failed to close ticket:', error);
            setOverlayState({ show: true, message: t('messageWindow.closeError'), success: false });
        } finally {
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 2000);
        }
    };

    const handleSendDraftMessage = async (messageId) => {
        try {
            const token = sessionStorage.getItem('access_token');
            await axios.post(`/api/messages/send`, { message_id: messageId }, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            });
            fetchMessages(); // Fetch messages to update the message list
        } catch (error) {
            console.error('Failed to send draft message:', error);
        }
    };

    const handleDeleteDraftMessage = async (messageId) => {
        try {
            const token = sessionStorage.getItem('access_token');
            await axios.post(`/api/messages/delete`, { message_id: messageId }, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            });
            fetchMessages(); // Fetch messages to update the message list
        } catch (error) {
            console.error('Failed to delete draft message:', error);
        }
    };

    const handleModifyDraftMessage = (message) => {
        setEditingMessage(message);
        setEditedContent(message.content);
    };

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    const handleSaveModifiedMessage = async () => {
        try {
            const token = sessionStorage.getItem('access_token');
            await axios.post(`/api/messages/edit`, {
                message_id: editingMessage._id,
                content: editedContent,
            }, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            });
            setEditingMessage(null);
            setEditedContent('');
            fetchMessages(); // Fetch messages to update the message list
        } catch (error) {
            console.error('Failed to modify draft message:', error);
        }
    };

    const handleCancelModification = () => {
        setEditingMessage(null);
        setEditedContent('');
    };

    // File overlay functions
    const handleFileClick = (file) => {
        setSelectedFile(file);
    };

    const closeFileOverlay = () => {
        setSelectedFile(null);
    };

    useEffect(() => {
        if (!userLoading && !user) {
            console.log('🔥 DEBUG: Redirecting because no user');
            navigate('/');
        }
    }, [userLoading, user, navigate]);

    if (loading) {
        console.log('🔥 DEBUG: Still loading, showing loading screen');
        return <div>{t('messageWindow.loading')}</div>;
    }

    if (!ticket) {
        console.log('🔥 DEBUG: No ticket found, showing error message');
        console.log('🔥 DEBUG: Current ticket state:', ticket);
        console.log('🔥 DEBUG: Current messages state:', messages);
        return <div>{t('messageWindow.failedToLoadTicket')}</div>;
    }

    console.log('🔥 DEBUG: About to render MessageWindow');
    console.log('🔥 DEBUG: ticket:', ticket);
    console.log('🔥 DEBUG: messages:', messages);
    console.log('🔥 DEBUG: contextData:', contextData);

    const isEditable = !['resolved', 'closed', 'ai_resolved'].includes(ticket.status);
    
    const handleLanguageChange = async (language) => {
        setTargetLanguage(language);
        if (language !== 'original') {
            setIsTranslating(true);
            await translateMessages(language);
            setIsTranslating(false);
        } else {
            setTranslatedMessages({});
        }
    };

    const translateMessages = async (language) => {
        try {
            const token = sessionStorage.getItem('access_token');
            const response = await axios.post('/api/messages/translate_messages', {
                messages: messages.map(m => ({ id: m._id, content: m.content })),
                target_language: language
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const translatedObj = {};
            response.data.translated_messages.forEach(m => {
                translatedObj[m.id] = m.content;
            });
            setTranslatedMessages(translatedObj);
        } catch (error) {
            console.error('Failed to translate messages:', error);
        }
    };

    const renderMessageContent = (message) => {
        const content = targetLanguage === 'original' ? message.content : (translatedMessages[message._id] || message.content);
        return formatMessageContent(content);
    };

    const renderMessage = (message) => {
        const isSent = message.sender === 'employee' || message.status === 'sent' || message.status === 'draft';
        const messageClass = `message ${isSent ? 'sent' : 'received'} ${message.status}`;
        
        // Check if this message is being edited
        const isBeingEdited = editingMessage && editingMessage._id === message._id;
        
        return (
            <div key={message._id} className={messageClass}>
                {message.status === 'automated' && (
                    <span className="message-status-badge automated">{t('messageWindow.automated')}</span>
                )}
                
                {isBeingEdited ? (
                    // Edit mode UI
                    <div className="message-edit-container">
                        <textarea
                            value={editedContent}
                            onChange={(e) => setEditedContent(e.target.value)}
                            className="message-edit-textarea"
                            rows={4}
                            placeholder={t('messageWindow.editMessage')}
                        />
                        <div className="edit-actions">
                            <button 
                                className="edit-action-button save" 
                                onClick={handleSaveModifiedMessage}
                            >
                                <FaCheck /> {t('messageWindow.save')}
                            </button>
                            <button 
                                className="edit-action-button cancel" 
                                onClick={handleCancelModification}
                            >
                                <FaTimes /> {t('messageWindow.cancel')}
                            </button>
                        </div>
                    </div>
                ) : (
                    // Normal message display
                    <div className="message-content">
                        {renderMessageContent(message)}
                    </div>
                )}
                
                <div className="message-meta">
                    <span className="message-status">
                        {message.status 
                            ? t(`messageWindow.statusOptions.${message.status}`)
                            : t('messageWindow.statusOptions.received')}
                    </span>
                    <span className="message-timestamp">{formatDate(message.created_at)}</span>
                </div>
                
                {message.status === 'draft' && isEditable && !isBeingEdited && (
                    <div className="draft-actions">
                        <button className="draft-message-button send" onClick={() => handleSendDraftMessage(message._id)}>
                            <FaPaperPlane /> {t('messageWindow.send')}
                        </button>
                        <button className="draft-message-button delete" onClick={() => handleDeleteDraftMessage(message._id)}>
                            <FaTimes /> {t('messageWindow.delete')}
                        </button>
                        <button className="draft-message-button modify" onClick={() => handleModifyDraftMessage(message)}>
                            <FaEdit /> {t('messageWindow.modify')}
                        </button>
                    </div>
                )}
            </div>
        );
    };

    const handleValidateCarrierMessage = async (conversationId, approved) => {
        setValidatingMessage(true);
        try {
            const token = sessionStorage.getItem('access_token');
            const response = await axios.post('/api/logistics/validate-carrier-message', 
                {
                    conversation_id: conversationId,
                    approved: approved
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            
            // Refresh the carriers conversation after validation
            await fetchCarriersConversation();
            
            setOverlayState({ 
                show: true, 
                message: approved ? t('messageWindow.messageApproved') : t('messageWindow.messageRejected'), 
                success: true 
            });
        } catch (error) {
            console.error('Failed to validate carrier message:', error);
            setOverlayState({ 
                show: true, 
                message: t('messageWindow.validationError'), 
                success: false 
            });
        } finally {
            setValidatingMessage(false);
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 2000);
        }
    };

    const getFileIcon = (mimeType) => {
        if (mimeType.includes('pdf')) {
            return <FaFilePdf />;
        } else if (mimeType.includes('image')) {
            return <FaFileImage />;
        } else if (mimeType.includes('zip') || mimeType.includes('rar')) {
            return <FaFileArchive />;
        } else if (mimeType.includes('code') || mimeType.includes('json')) {
            return <FaFileCode />;
        }
        return <FaFileAlt />;
    };

    const getFileClass = (mimeType) => {
        if (mimeType.includes('pdf')) {
            return 'pdf';
        } else if (mimeType.includes('image')) {
            return 'image';
        }
        return 'document';
    };

    // Add this new component for the metadata tooltip
    const FileMetadataTooltip = ({ metadata }) => {
        if (!metadata || Object.keys(metadata).length === 0) return null;
        
        return (
            <div className="file-metadata-content">
                {Object.entries(metadata).map(([key, value]) => (
                    value && (
                        <div key={key} className={`metadata-item metadata-${key}`}>
                            <strong>{t(`messageWindow.metadata.${key}`, key)}:</strong> {value}
                        </div>
                    )
                ))}
            </div>
        );
    };

    // Add handler for warehouse email submission
    const handleWarehouseEmailSubmit = async (e) => {
        e.preventDefault();
        setIsProcessing(true);
        try {
            const token = sessionStorage.getItem('access_token');
            const formData = new FormData();
            formData.append('subject', warehouseEmailForm.subject);
            formData.append('content', warehouseEmailForm.content);
            formData.append('ticket_id', ticketId);
            formData.append('recipient_email', warehouseEmailForm.recipient_email);
            formData.append('asst_id', user.assistant_id);

            const response = await axios.post('/api/webhook/warehouse-email', 
                formData,
                {
                    headers: { 
                        Authorization: `Bearer ${token}`,
                    }
                }
            );

            setOverlayState({
                show: true,
                message: t('messageWindow.warehouseEmailSent'),
                success: true
            });
            
            // Reset form
            setWarehouseEmailForm({
                subject: '',
                content: '',
                recipient_email: ''
            });
            
        } catch (error) {
            console.error('Failed to send warehouse email:', error);
            setOverlayState({
                show: true,
                message: t('messageWindow.warehouseEmailError'),
                success: false
            });
        } finally {
            setIsProcessing(false);
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 2000);
        }
    };

    // Add file selection handler
    const handleFileSelection = (fileUrl) => {
        setSelectedTicketFiles(prev => {
            if (prev.includes(fileUrl)) {
                return prev.filter(url => url !== fileUrl);
            } else {
                return [...prev, fileUrl];
            }
        });
    };

    // Add handler for niveau2 email submission
    const handleNiveau2EmailSubmit = async (e) => {
        e.preventDefault();
        setIsProcessing(true);
        try {
            const token = sessionStorage.getItem('access_token');
            const formData = new FormData();
            formData.append('subject', niveau2EmailForm.subject);
            formData.append('content', niveau2EmailForm.content);
            formData.append('ticket_id', ticketId);
            formData.append('recipient_email', niveau2EmailForm.recipient_email);
            formData.append('asst_id', user.assistant_id);
            formData.append('file_urls', JSON.stringify(selectedTicketFiles));

            const response = await axios.post('/api/webhook/niveau2-email', 
                formData,
                {
                    headers: { 
                        Authorization: `Bearer ${token}`,
                    }
                }
            );

            setOverlayState({
                show: true,
                message: t('messageWindow.niveau2EmailSent'),
                success: true
            });
            
            // Reset form and selected files
            setNiveau2EmailForm({
                subject: '',
                content: '',
                recipient_email: '',
                selectedFiles: []
            });
            setSelectedTicketFiles([]);
            
        } catch (error) {
            console.error('Failed to send niveau2 email:', error);
            setOverlayState({
                show: true,
                message: t('messageWindow.niveau2EmailError'),
                success: false
            });
        } finally {
            setIsProcessing(false);
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 2000);
        }
    };

    // Update this component for rendering warehouse messages
    const WarehouseConversation = ({ conversation }) => {
        if (!conversation || !conversation.messages || conversation.messages.length === 0) {
            return <div className="empty-conversation">{t('messageWindow.noWarehouseMessages')}</div>;
        }

        return (
            <div className="warehouse-conversation">
                <div className="warehouse-conversation-tracking">
                    <span className="tracking-badge warehouse">
                        <FaWarehouse /> {t('messageWindow.warehouseConversation')}
                    </span>
                </div>
                
                <div className="warehouse-conversation-content">
                    <div className="warehouse-conversation-messages">
                        {conversation.messages.map((message, index) => (
                            <div 
                                key={index} 
                                className={`warehouse-conversation-message warehouse-conversation-message--${message.sender_type === 'warehouse' ? 'received' : 'sent'}`}
                            >
                                <div className="warehouse-conversation-message__sender">
                                    {message.direction === 'final_response' 
                                        ? t('messageWindow.warehouse') 
                                        : t('messageWindow.us')}
                                </div>
                                <div className="warehouse-conversation-message__content">
                                    <div className="warehouse-response">
                                        <div className="warehouse-response__details-content">
                                            {formatMessageContent(message.content)}
                                        </div>
                                    </div>
                                </div>
                                <div className="warehouse-conversation-message__meta">
                                    <span className="warehouse-conversation-message__timestamp">
                                        {formatDate(message.timestamp)}
                                    </span>
                                    <span className="warehouse-conversation-message__status">
                                        {message.status || t('messageWindow.statusOptions.sent')}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="warehouse-refresh">
                    <button onClick={fetchWarehouseConversation} className="refresh-button">
                        {t('messageWindow.refreshConversation')}
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="app-container">
            <div className="message-window-layout">
                <div className="ticket-header">
                    <div className="ticket-title-row">
                        <div className="ticket-title-container">
                            <h2>{ticket.subject}</h2>
                        </div>
                        <div className="header-actions">
                            {ticketFiles.length > 0 && (
                                <div className="ticket-file-indicators">
                                    {ticketFiles.map((file, index) => (
                                        <div
                                            key={index}
                                            className={`ticket-file-indicator ${getFileClass(file.mime_type)}`}
                                            onClick={() => handleFileClick(file)}
                                        >
                                            {getFileIcon(file.mime_type)}
                                            <div className="ticket-file-tooltip">
                                                <p className="tooltip-filename">{file.name}</p>
                                                {file.metadata && Object.keys(file.metadata).length > 0 && (
                                                    <FileMetadataTooltip metadata={file.metadata} />
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className="action-menu-container" ref={actionMenuRef}>
                                <button 
                                    className="action-menu-trigger"
                                    onClick={() => setShowActionMenu(!showActionMenu)}
                                >
                                    <FaEllipsisV />
                                </button>
                                {showActionMenu && (
                                    <div className="action-menu">
                                        {isEditable && (
                                            <>
                                                <button onClick={handleResolveTicket}>
                                                    <FaCheck /> {t('messageWindow.resolveTicket')}
                                                </button>
                                                <button onClick={handleCloseTicket}>
                                                    <FaTimes /> {t('messageWindow.closeTicket')}
                                                </button>
                                            </>
                                        )}
                                        <div className="action-menu-item" 
                                             onClick={() => setShowLanguageMenu(!showLanguageMenu)}>
                                            <span>
                                                <FaLanguage /> {t('messageWindow.translate')}
                                            </span>
                                            {showLanguageMenu && (
                                                <div className="language-submenu">
                                                    <button onClick={() => {
                                                        handleLanguageChange('original');
                                                        setShowLanguageMenu(false);
                                                    }}>
                                                        {t('messageWindow.original')}
                                                    </button>
                                                    <button onClick={() => {
                                                        handleLanguageChange('en');
                                                        setShowLanguageMenu(false);
                                                    }}>English</button>
                                                    <button onClick={() => {
                                                        handleLanguageChange('fr');
                                                        setShowLanguageMenu(false);
                                                    }}>Français</button>
                                                    <button onClick={() => {
                                                        handleLanguageChange('es');
                                                        setShowLanguageMenu(false);
                                                    }}>Español</button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="ticket-meta">
                        <span className={`status-badge ${ticket.status}`}>
                            {t(`messageWindow.statusOptions.${ticket.status}`, ticket.status)}
                        </span>
                        <span className="ticket-employee">
                            {t('messageWindow.employee')}: {employee}
                        </span>
                        <div className="ticket-indicators">
                            {contextData && (
                                <>
                                    <div className="ticket-indicator tracking" title={t('messageWindow.masterTracking')}>
                                        <FaBarcode />
                                        <span>{contextData.shipping_info.master_tracking || t('messageWindow.noTracking')}</span>
                                    </div>
                                    {contextData.shipping_info.external_ticket_id && (
                                        <div className="ticket-indicator tracking" title={t('messageWindow.externalTicketId', 'External Ticket ID')}>
                                            <FaIdCard />
                                            <span>{contextData.shipping_info.external_ticket_id}</span>
                                        </div>
                                    )}
                                    <div className="ticket-indicator tracking" title={t('messageWindow.topic')}>
                                        <FaTag />
                                        <span>{contextData.shipping_info.topic || t('messageWindow.noTopic')}</span>
                                    </div>
                                    <div className="ticket-indicator client" title={t('messageWindow.clientName')}>
                                        <FaUser />
                                        <span>{contextData.client_info.name || t('messageWindow.noClient')}</span>
                                    </div>
                                    <div className="ticket-indicator carrier" title={t('messageWindow.carrierName')}>
                                        <FaTruck />
                                        <span>{contextData.shipping_info.carrier_name || t('messageWindow.noCarrier')}</span>
                                    </div>
                                    <div className={`ticket-indicator status ${contextData.shipping_info.is_shipped ? '' : 'off'}`} 
                                         title={t('messageWindow.shipped')}>
                                        <FaShippingFast />
                                        <span>{t('messageWindow.shipped')}</span>
                                    </div>
                                    <div className={`ticket-indicator status ${contextData.shipping_info.is_delivered ? '' : 'off'}`}
                                         title={t('messageWindow.delivered')}>
                                        <FaBox />
                                        <span>{t('messageWindow.delivered')}</span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                
                <div className="message-window-content">
                    <div className="message-window-messages">
                        {messages.map((message) => renderMessage(message))}
                    </div>

                    {isEditable && (
                        <div className="input-container">
                            <textarea
                                value={newMessage}
                                onChange={(e) => {
                                    setNewMessage(e.target.value);
                                    // Auto-grow functionality
                                    e.target.style.height = '40px';
                                    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
                                }}
                                placeholder={t('messageWindow.typeYourResponse')}
                                className="message-window-textarea"
                                rows={1}
                            />
                            <div className="response-actions">
                                <button className="message-window-action-button message-window-send" onClick={handleSendMessage}>
                                    <FaPaperPlane /> {t('messageWindow.send')}
                                </button>
                                <button className="message-window-action-button message-window-ai-help" onClick={handleGetAssistantResponse}>
                                    <FaLightbulb /> {t('messageWindow.aiHelp')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="side-panel">
                <div className="tab-buttons">
                    <button 
                        className={`tab-button ${activeTab === 'carriers' ? 'active' : ''}`}
                        onClick={() => setActiveTab('carriers')}
                        title={t('messageWindow.carriersConversation')}
                    >
                        <FaComments />
                    </button>
                    <button 
                        className={`tab-button ${activeTab === 'manual' ? 'active' : ''}`}
                        onClick={() => setActiveTab('manual')}
                        title={t('messageWindow.manualConversation')}
                    >
                        <FaHandPaper />
                    </button>
                    <button 
                        className={`tab-button ${activeTab === 'user' ? 'active' : ''}`}
                        onClick={() => setActiveTab('user')}
                        title={t('messageWindow.userInfo')}
                    >
                        <FaUser />
                    </button>
                    <button 
                        className={`tab-button ${activeTab === 'track' ? 'active' : ''}`}
                        onClick={() => setActiveTab('track')}
                        title={t('messageWindow.trackingInfo')}
                    >
                        <FaShippingFast />
                    </button>
                    <button 
                        className={`tab-button ${activeTab === 'warehouse' ? 'active' : ''}`}
                        onClick={() => setActiveTab('warehouse')}
                        title={t('messageWindow.warehouseEmail')}
                    >
                        <FaWarehouse />
                    </button>
                    <button 
                        className={`tab-button ${activeTab === 'niveau2' ? 'active' : ''}`}
                        onClick={() => setActiveTab('niveau2')}
                        title={t('messageWindow.niveau2Email')}
                    >
                        <FaEnvelope />
                    </button>
                </div>
                
                <div className="tab-content">
                    {activeTab === 'carriers' && (
                        <CarriersConversation 
                            conversation={carriersConversation} 
                            onValidateMessage={handleValidateCarrierMessage}
                            isValidating={validatingMessage}
                            onRefresh={fetchCarriersConversation}
                        />
                    )}
                    {activeTab === 'manual' && (
                        <ManualConversation 
                            onRefresh={fetchCarriersConversation}
                            ticketFiles={ticketFiles}
                            ticket={ticket}
                        />
                    )}
                    {activeTab === 'user' && contextData && (
                        <div className="user-tab">
                            <div className="user-section">
                                <h3>{t('messageWindow.recipientInfo')}</h3>
                                {contextData.recipient_info.company && (
                                    <p><FaBuilding /><strong>{t('messageWindow.company')}:</strong> {contextData.recipient_info.company}</p>
                                )}
                                <p><FaUser /><strong>{t('messageWindow.name')}:</strong> {contextData.recipient_info.name}</p>
                                <h4>{t('messageWindow.address')}</h4>
                                <p><FaMapMarkerAlt /><strong>{t('messageWindow.street')}:</strong> {contextData.recipient_info.address.street1}</p>
                                {contextData.recipient_info.address.street2 && 
                                    <p><FaMapMarkerAlt /><strong>{t('messageWindow.street2')}:</strong> {contextData.recipient_info.address.street2}</p>
                                }
                                <p><FaCity /><strong>{t('messageWindow.city')}:</strong> {contextData.recipient_info.address.postcode} {contextData.recipient_info.address.city}</p>
                                <p><FaGlobe /><strong>{t('messageWindow.country')}:</strong> {contextData.recipient_info.address.country}</p>
                                <h4>{t('messageWindow.contact')}</h4>
                                <p><FaPhone /><strong>{t('messageWindow.phone')}:</strong> {contextData.recipient_info.contact.phone}</p>
                                <p><FaEnvelope /><strong>{t('messageWindow.email')}:</strong> {contextData.recipient_info.contact.email}</p>
                                {contextData.shipping_info.label_url && (
                                    <p><strong>{t('messageWindow.labelUrl')}:</strong> 
                                        <a href={contextData.shipping_info.label_url} target="_blank" rel="noopener noreferrer">
                                            {t('messageWindow.viewLabel')}
                                        </a>
                                    </p>
                                )}
                            </div>

                            <div className="user-section">
                                <h3>{t('messageWindow.clientInfo')}</h3>
                                <p><FaIdCard /><strong>{t('messageWindow.clientId')}:</strong> {contextData.client_info.id}</p>
                                <p><FaUser /><strong>{t('messageWindow.clientName')}:</strong> {contextData.client_info.name}</p>
                                <p><FaInfo /><strong>{t('messageWindow.clientStatus')}:</strong> {contextData.client_info.status}</p>
                            </div>

                            <div className="user-section">
                                <h3>{t('messageWindow.packageInfo')}</h3>
                                <p><FaBarcode /><strong>{t('messageWindow.reference')}:</strong> {contextData.order_info.reference}</p>
                                <div className="package-content">
                                    <FaBox />
                                    <p>{t('messageWindow.packageContent')}:</p>
                                    <div className="package-content-truncated">
                                        {contextData.order_info.package_content}
                                    </div>
                                    <div className="package-content-full">
                                        {contextData.order_info.package_content}
                                    </div>
                                </div>
                                <p><FaMoneyBillWave /><strong>{t('messageWindow.goodsValue')}:</strong> {contextData.order_info.goods_value} {contextData.order_info.currency}</p>
                                <p><FaBoxOpen /><strong>{t('messageWindow.packageValue')}:</strong> {contextData.order_info.package_value} {contextData.order_info.currency}</p>
                                <p><FaShieldAlt /><strong>{t('messageWindow.insuranceValue')}:</strong> {contextData.order_info.insurance_value} {contextData.order_info.currency}</p>
                                {contextData.order_info.ht_price !== null && (
                                    <p><FaReceipt /><strong>{t('messageWindow.htPrice')}:</strong> {contextData.order_info.ht_price} {contextData.order_info.currency}</p>
                                )}
                            </div>

                            {contextData.compatibility_result && (
                                <div className="user-section">
                                    <h3><FaExclamationTriangle /> {t('messageWindow.compatibilityResult', 'Compatibility Result')}</h3>
                                    <div className="compatibility-result-content">
                                        {Object.entries(contextData.compatibility_result).map(([key, value]) => {
                                            if (value === null || value === undefined || value === '') return null;
                                            
                                            // Special handling for parts array
                                            if (key === 'parts' && Array.isArray(value)) {
                                                return (
                                                    <div key={key} className="compatibility-parts">
                                                        <p><strong>{t('messageWindow.compatibility.parts', 'Parts')}:</strong></p>
                                                        <div className="parts-list">
                                                            {value.map((part, index) => (
                                                                <div key={index} className="part-item">
                                                                    <div className="part-details">
                                                                        <p className="part-group">
                                                                            <strong>{t('messageWindow.compatibility.partGroup', 'Part Group')}:</strong> {part.part_group}
                                                                        </p>
                                                                        <p className="part-number">
                                                                            <strong>{t('messageWindow.compatibility.partNumber', 'Part Number')}:</strong> {part.part_number}
                                                                        </p>
                                                                        {part.part_name && (
                                                                            <p className="part-name">
                                                                                <strong>{t('messageWindow.compatibility.partName', 'Part Name')}:</strong> {part.part_name}
                                                                            </p>
                                                                        )}
                                                                        {part.url && (
                                                                            <p className="part-url">
                                                                                <strong>{t('messageWindow.compatibility.partUrl', 'Link')}:</strong>{' '}
                                                                                <a 
                                                                                    href={part.url} 
                                                                                    target="_blank" 
                                                                                    rel="noopener noreferrer"
                                                                                    className="part-link"
                                                                                >
                                                                                    {t('messageWindow.compatibility.viewPart', 'View Part')}
                                                                                </a>
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            
                                            // Handle different value types for other fields
                                            let displayValue = value;
                                            if (typeof value === 'boolean') {
                                                displayValue = value ? t('messageWindow.yes', 'Yes') : t('messageWindow.no', 'No');
                                            } else if (typeof value === 'object') {
                                                displayValue = JSON.stringify(value, null, 2);
                                            }
                                            
                                            return (
                                                <p key={key}>
                                                    <strong>{t(`messageWindow.compatibility.${key}`, key.replace(/_/g, ' '))}:</strong> {displayValue}
                                                </p>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    {activeTab === 'track' && contextData && (
                        <div className="track-tab">
                            <div className="tracking-section">
                                <h3>{t('messageWindow.shippingInfo')}</h3>
                                
                                {contextData.shipping_info.tracking_info && (
                                    <div className="tracking-info">
                                        <p><strong>{t('messageWindow.currentStatus')}:</strong> {contextData.shipping_info.tracking_info.status}</p>
                                        
                                        {contextData.shipping_info.tracking_info.events && 
                                         contextData.shipping_info.tracking_info.events.length > 0 && (
                                            <>
                                                <h4>{t('messageWindow.trackingHistory')}</h4>
                                                <div className="tracking-events">
                                                    {contextData.shipping_info.tracking_info.events.map((event, index) => (
                                                        <div key={index} className="tracking-event">
                                                            <div className="event-date">
                                                                {new Date(event.date).toLocaleString()}
                                                            </div>
                                                            {event.location && (
                                                                <div className="event-location">
                                                                    {event.location}
                                                                </div>
                                                            )}
                                                            <div className="event-status">
                                                                {event.status}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    {activeTab === 'warehouse' && (
                        <div className="warehouse-tab">
                            {warehouseLoading ? (
                                <div className="loading-indicator">{t('messageWindow.loading')}</div>
                            ) : warehouseConversation && warehouseConversation.messages && warehouseConversation.messages.length > 0 ? (
                                <WarehouseConversation conversation={warehouseConversation} />
                            ) : (
                                <form onSubmit={handleWarehouseEmailSubmit} className="warehouse-email-form">
                                    <div className="form-group">
                                        <label htmlFor="recipient_email">{t('messageWindow.recipientEmail')}</label>
                                        <input
                                            type="email"
                                            id="recipient_email"
                                            value={warehouseEmailForm.recipient_email}
                                            onChange={(e) => setWarehouseEmailForm(prev => ({
                                                ...prev,
                                                recipient_email: e.target.value
                                            }))}
                                            required
                                            className="warehouse-input"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="subject">{t('messageWindow.emailSubject')}</label>
                                        <input
                                            type="text"
                                            id="subject"
                                            value={warehouseEmailForm.subject}
                                            onChange={(e) => setWarehouseEmailForm(prev => ({
                                                ...prev,
                                                subject: e.target.value
                                            }))}
                                            required
                                            className="warehouse-input"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="content">{t('messageWindow.emailContent')}</label>
                                        <textarea
                                            id="content"
                                            value={warehouseEmailForm.content}
                                            onChange={(e) => setWarehouseEmailForm(prev => ({
                                                ...prev,
                                                content: e.target.value
                                            }))}
                                            required
                                            className="warehouse-textarea"
                                            rows={6}
                                        />
                                    </div>
                                    <button 
                                        type="submit" 
                                        className="submit-button"
                                        disabled={isProcessing}
                                    >
                                        {isProcessing ? (
                                            t('messageWindow.sending')
                                        ) : (
                                            <>
                                                <FaPaperPlane /> {t('messageWindow.sendToWarehouse')}
                                            </>
                                        )}
                                    </button>
                                </form>
                            )}
                        </div>
                    )}
                    {activeTab === 'niveau2' && (
                        <div className="niveau2-tab">
                            <form onSubmit={handleNiveau2EmailSubmit} className="niveau2-email-form">
                                <div className="form-group">
                                    <label htmlFor="niveau2_recipient_email">{t('messageWindow.recipientEmail')}</label>
                                    <input
                                        type="email"
                                        id="niveau2_recipient_email"
                                        value={niveau2EmailForm.recipient_email}
                                        onChange={(e) => setNiveau2EmailForm(prev => ({
                                            ...prev,
                                            recipient_email: e.target.value
                                        }))}
                                        required
                                        className="niveau2-input"
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="niveau2_subject">{t('messageWindow.emailSubject')}</label>
                                    <input
                                        type="text"
                                        id="niveau2_subject"
                                        value={niveau2EmailForm.subject}
                                        onChange={(e) => setNiveau2EmailForm(prev => ({
                                            ...prev,
                                            subject: e.target.value
                                        }))}
                                        required
                                        className="niveau2-input"
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="niveau2_content">{t('messageWindow.emailContent')}</label>
                                    <textarea
                                        id="niveau2_content"
                                        value={niveau2EmailForm.content}
                                        onChange={(e) => setNiveau2EmailForm(prev => ({
                                            ...prev,
                                            content: e.target.value
                                        }))}
                                        required
                                        className="niveau2-textarea"
                                        rows={6}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>{t('messageWindow.selectFiles')}</label>
                                    <div className="file-selection-grid">
                                        {ticketFiles.map((file, index) => (
                                            <div 
                                                key={index}
                                                className={`file-selection-item ${selectedTicketFiles.includes(file.url) ? 'selected' : ''}`}
                                                onClick={() => handleFileSelection(file.url)}
                                            >
                                                {getFileIcon(file.mime_type)}
                                                <span className="file-name">{file.name}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <button 
                                    type="submit" 
                                    className="niveau2-submit-button"
                                    disabled={isProcessing}
                                >
                                    {isProcessing ? (
                                        t('messageWindow.sending')
                                    ) : (
                                        <>
                                            <FaPaperPlane /> {t('messageWindow.sendToNiveau2')}
                                        </>
                                    )}
                                </button>
                            </form>
                        </div>
                    )}
                </div>
            </div>

            {/* File Overlay */}
            {selectedFile && (
                <div className="file-overlay">
                    <div className="file-overlay-content">
                        <button className="close-overlay" onClick={closeFileOverlay}>
                            <FaTimesCircle size={30} />
                        </button>
                        {selectedFile.mime_type === 'application/pdf' ? (
                            <object
                                data={selectedFile.url}
                                type="application/pdf"
                                width="100%"
                                height="100%"
                            >
                                <p>
                                    {t('messageWindow.unableToDisplayFile')}. <a href={selectedFile.url}> {t('messageWindow.downloadFile')}</a>
                                </p>
                            </object>
                        ) : (
                            <iframe 
                                src={selectedFile.url} 
                                title={selectedFile.name} 
                                width="100%" 
                                height="100%" 
                                frameBorder="0"
                            ></iframe>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MessageWindow;

